import React, { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useSpellCheck from './useSpellCheck';
import './SpellTextarea.css';

/**
 * Textarea с подчёркиванием опечаток и контекстным меню исправлений.
 * Props:
 *   value, onChange(newValue, e), placeholder, rows, className,
 *   onKeyDown, disabled, autoFocus, spellCheckEnabled (default true),
 *   singleLine (если true — Enter не вставляет перенос, обрабатывается через onKeyDown),
 *   autoResize (если true — высота textarea растёт под содержимое до max-height из CSS)
 */
const SpellTextarea = forwardRef(function SpellTextarea(
  {
    value,
    onChange,
    placeholder = '',
    rows = 3,
    className = '',
    onKeyDown,
    disabled,
    autoFocus,
    spellCheckEnabled = true,
    singleLine = false,
    autoResize = false,
    onBlur,
    onFocus,
  },
  ref
) {
  const taRef = useRef(null);
  const overlayRef = useRef(null);
  const wrapperRef = useRef(null);
  const [popover, setPopover] = useState(null); // { issue, x, y }

  useImperativeHandle(ref, () => ({
    focus: () => taRef.current?.focus(),
    blur: () => taRef.current?.blur(),
    get element() { return taRef.current; },
  }), []);

  const issues = useSpellCheck(value, { enabled: spellCheckEnabled });

  // Синхронизация скролла overlay с textarea
  const syncScroll = useCallback(() => {
    if (!taRef.current || !overlayRef.current) return;
    overlayRef.current.scrollTop = taRef.current.scrollTop;
    overlayRef.current.scrollLeft = taRef.current.scrollLeft;
  }, []);

  useLayoutEffect(() => { syncScroll(); }, [value, issues, syncScroll]);

  // Авторост по содержимому. Сбрасываем height в auto, чтобы scrollHeight отдал
  // реальную нужную высоту, потом ставим её. max-height в CSS ограничит сверху,
  // дальше появится скролл внутри textarea (overlay растёт за textarea через inset:0).
  useLayoutEffect(() => {
    if (!autoResize) return;
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = ta.scrollHeight + 'px';
  }, [value, autoResize]);

  const handleChange = (e) => {
    setPopover(null);
    onChange?.(e.target.value, e);
  };

  const handleKeyDown = (e) => {
    if (singleLine && e.key === 'Enter' && !e.shiftKey) {
      // Не позволяем вставить перенос строки в single-line режиме —
      // потребитель должен сам обработать Enter через onKeyDown
    }
    onKeyDown?.(e);
  };

  // Рендерим overlay: текст с обёрнутыми span'ами для опечаток
  const overlayChildren = useMemo(() => {
    if (!issues.length) return value;
    const parts = [];
    let cursor = 0;
    issues
      .slice()
      .sort((a, b) => a.offset - b.offset)
      .forEach((issue, idx) => {
        if (issue.offset > cursor) parts.push(value.slice(cursor, issue.offset));
        parts.push(
          <span key={idx} className="ssp-mistake" data-idx={idx}>
            {value.slice(issue.offset, issue.offset + issue.length)}
          </span>
        );
        cursor = issue.offset + issue.length;
      });
    if (cursor < value.length) parts.push(value.slice(cursor));
    return parts;
  }, [value, issues]);

  // Клик по подчёркнутому слову → popover с suggestions
  const handleOverlayClick = (e) => {
    const target = e.target;
    if (target?.classList?.contains('ssp-mistake')) {
      const idx = parseInt(target.getAttribute('data-idx'), 10);
      const issue = issues[idx];
      if (!issue) return;
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const rect = target.getBoundingClientRect();
      setPopover({
        issue,
        // Якорь у верхнего края слова. Сам попап через CSS transform смещается вверх,
        // чтобы оказаться НАД словом (важно для нижнего инпута в чате — иначе
        // вылезает за viewport).
        x: rect.left - wrapperRect.left,
        y: rect.top - wrapperRect.top - 4,
      });
    } else {
      setPopover(null);
    }
  };

  const replaceWord = (suggestion) => {
    if (!popover) return;
    const { issue } = popover;
    const newValue = value.slice(0, issue.offset) + suggestion + value.slice(issue.offset + issue.length);
    onChange?.(newValue, null);
    setPopover(null);
    // Возвращаем фокус
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (!ta) return;
      ta.focus();
      const pos = issue.offset + suggestion.length;
      try { ta.setSelectionRange(pos, pos); } catch (_) {}
    });
  };

  return (
    <div className={'ssp-wrapper ' + className} ref={wrapperRef}>
      <div
        className="ssp-overlay"
        ref={overlayRef}
        aria-hidden="true"
        onMouseDown={(e) => {
          // Если кликнули по слову-ошибке — переключаем в режим popover (textarea не получает фокус сразу)
          if (e.target?.classList?.contains('ssp-mistake')) {
            e.preventDefault();
            handleOverlayClick(e);
          }
        }}
      >
        {overlayChildren}
        {/* Терминальный пустой узел, чтобы трейлинг \n не схлопывался */}
        {'\u200b'}
      </div>
      <textarea
        ref={taRef}
        className="ssp-textarea"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onScroll={syncScroll}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        autoFocus={autoFocus}
        spellCheck={false}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      {popover && (
        <div className="ssp-popover" style={{ left: popover.x, top: popover.y }}>
          {popover.issue.suggestions.length > 0 ? (
            popover.issue.suggestions.map((s) => (
              <button key={s} type="button" className="ssp-suggestion" onClick={() => replaceWord(s)}>{s}</button>
            ))
          ) : (
            <span className="ssp-no-suggest">Нет вариантов</span>
          )}
          <button type="button" className="ssp-close" onClick={() => setPopover(null)} aria-label="Закрыть">×</button>
        </div>
      )}
    </div>
  );
});

export default SpellTextarea;
